import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { z, ZodSchema } from 'zod';
import { sanitizeRequestBody } from '../sanitize';

// Standard error response schema matching backend ErrorResponseDto
const ValidationErrorItemSchema = z.object({
  field: z.string(),
  message: z.string(),
  value: z.unknown().optional(),
});

export const ApiErrorSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  error: z.string().optional(),
  timestamp: z.string().optional(),
  path: z.string().optional(),
  validationErrors: z.array(ValidationErrorItemSchema).optional(),
  /**
   * ATL-1487: the backend's machine-readable refusal code, when the exception was thrown as
   * `{ code, message }` (e.g. `foreign_unique_conflict`, `money_leg_buried`). The dual-write
   * reporter keys a refused write's fingerprint and event on it; without it every refusal of one
   * operation is a single, unlabelled bucket.
   */
  code: z.string().optional(),
  /**
   * ATL-1445 stage 3 (P8): the organization a write-master refusal is about — B1's
   * `WRITE_MASTER_SUPABASE` body names it (`./write-master-refusal.ts`). Absent from every other
   * error body, and then absent here too: no key is added for a body that does not carry it.
   */
  organizationFirestoreId: z.string().optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Join a NestJS ValidationPipe message (string | string[]) into one string. */
const joinMessages = (m: unknown): string | undefined => {
  if (typeof m === 'string') return m;
  if (Array.isArray(m) && m.length > 0 && m.every((x) => typeof x === 'string')) {
    return m.join('; ');
  }
  return undefined;
};

/** `{ organizationFirestoreId }` from the envelope's inner body (else the outer one), or nothing. */
const organizationFirestoreIdOf = (
  inner: Record<string, unknown> | undefined,
  outer: Record<string, unknown>,
): { organizationFirestoreId?: string } => {
  const value =
    typeof inner?.organizationFirestoreId === 'string'
      ? inner.organizationFirestoreId
      : typeof outer.organizationFirestoreId === 'string'
        ? outer.organizationFirestoreId
        : undefined;
  return value === undefined ? {} : { organizationFirestoreId: value };
};

/**
 * Normalize a backend error body into ApiError (ATL-566).
 *
 * The backend's AllExceptionsFilter wraps every HttpException as
 * `{ statusCode, timestamp, path, message: <getResponse()> }`, and for a
 * ValidationPipe 400 `getResponse()` is itself an object whose `message` is a
 * `string[]` of constraint texts. That nested shape fails the flat
 * ApiErrorSchema, and previously the catch-all fallback DISCARDED
 * `error.response.data` — so callers (and Sentry, via dual-write reporting)
 * only ever saw the generic axios "Request failed with status code 400" with
 * no hint of which field was rejected. Unwrap the envelope so the real
 * constraint text survives into ApiRequestError.
 */
export function normalizeApiErrorBody(
  data: unknown,
  fallbackStatus: number,
  fallbackError: string,
  fallbackMessage: string,
): ApiError {
  const direct = ApiErrorSchema.safeParse(data);
  if (direct.success) return direct.data;

  if (data && typeof data === 'object') {
    const outer = data as Record<string, unknown>;
    // AllExceptionsFilter envelope: `message` holds the original HttpException body.
    const inner =
      outer.message && typeof outer.message === 'object' && !Array.isArray(outer.message)
        ? (outer.message as Record<string, unknown>)
        : undefined;
    // Prefer the inner (real) message; fall back to a bare string[] at top level.
    const message = joinMessages(inner?.message) ?? joinMessages(outer.message);
    if (message) {
      const validationErrors = z
        .array(ValidationErrorItemSchema)
        .safeParse(inner?.validationErrors ?? outer.validationErrors);
      return {
        statusCode: typeof outer.statusCode === 'number' ? outer.statusCode : fallbackStatus,
        message,
        error:
          typeof inner?.error === 'string'
            ? inner.error
            : typeof outer.error === 'string'
              ? outer.error
              : fallbackError,
        timestamp: typeof outer.timestamp === 'string' ? outer.timestamp : undefined,
        path: typeof outer.path === 'string' ? outer.path : undefined,
        validationErrors: validationErrors.success ? validationErrors.data : undefined,
        // The inner body is the exception's own (`ConflictException({ code, message })`); a flat
        // top-level code is kept for a body that carries one without the envelope.
        code:
          typeof inner?.code === 'string'
            ? inner.code
            : typeof outer.code === 'string'
              ? outer.code
              : undefined,
        // ATL-1445 P8: kept only when the body carries it (B1's write-master refusal), so every other
        // error normalizes to exactly the object it did before — not even an `undefined` key is added.
        ...organizationFirestoreIdOf(inner, outer),
      };
    }
  }

  return { statusCode: fallbackStatus, message: fallbackMessage, error: fallbackError };
}

export class ApiValidationError extends Error {
  constructor(
    message: string,
    public errors: z.ZodError['errors'],
    public type: 'request' | 'response',
    public path: string,
  ) {
    super(message);
    this.name = 'ApiValidationError';
  }
}

/**
 * The request as the caller supplied it (post date-serialization, pre-axios-transform) —
 * enough to re-issue the identical write later through `TypedApiClient.replayRequest`.
 *
 * Attached to every error thrown out of `request()` that represents a write that REACHED
 * the transport (HTTP-status failures via `ApiRequestError.requestConfig`, network/timeout
 * failures via the same property patched onto the raw axios error). The dual-write outbox
 * (ATL-782) persists this to retry transient failures after the app restarts; without it a
 * 5xx/timeout on an already-committed Firestore write is lost the moment it fails.
 */
export interface ReplayableRequestConfig {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
}

/**
 * ATL-1445 stage 3 (plan §1, §4) — the header that marks a write as the WRITE MASTER's, i.e. sent
 * backend-first by a device whose organization is Supabase-master, from outbox v2. Without it the
 * backend treats the request as today's Firestore-first MIRROR (and backend B1 refuses a mirror for a
 * Supabase-master organization with 409 `WRITE_MASTER_SUPABASE`).
 *
 * Sent only by `replayRequest(config, { writeMode: 'master' })`. It is never persisted: the outbox
 * stores requests without headers (see {@link ReplayableRequestConfig}), and the mode is decided by
 * the queue at send time.
 *
 * ⚠️ WEB: this is a non-safelisted request header, so a browser sends a CORS preflight and drops the
 * request unless the backend lists it in `allowedHeaders`. It is listed (`apps/atlas-backend/src/main.ts`,
 * since P3; pinned by `cors-allowed-headers.atl1445.spec.ts`), so a web master send passes the
 * preflight. Nothing sends it yet (stage 3 ships dark).
 */
export const WRITE_MODE_HEADER = 'X-Atlas-Write-Mode';

/** The only write mode a request can declare; the absence of the header is today's mirror. */
export type WriteMode = 'master';

/** Exhaustive over {@link WriteMode}: a new mode is a compile error here, never an unset header. */
function writeModeHeaderValue(mode: WriteMode): string {
  switch (mode) {
    case 'master':
      return 'master';
  }
}

export interface ReplayRequestOptions {
  /** Declare the send as the write master's (outbox v2). Omitted: no header, today's behaviour. */
  writeMode?: WriteMode;
}

export class ApiRequestError extends Error {
  /** See {@link ReplayableRequestConfig} — set by `request()` when the failing call is re-issuable. */
  public requestConfig?: ReplayableRequestConfig;

  constructor(
    message: string,
    public statusCode: number,
    public error: ApiError,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export interface TypedApiClientConfig {
  baseURL: string;
  /**
   * Resolve the bearer token for a request.
   * `forceRefresh` is passed as `true` by the 401 retry path so the provider can
   * bypass any cached token (e.g. Firebase `getIdToken(true)`) — the SDK otherwise
   * only auto-refreshes within 5 min of expiry, which leaves a stale-token window
   * exposed to clock skew / borderline expiry (ATL-526).
   */
  getAuthToken: (forceRefresh?: boolean) => Promise<string | null>;
  timeout?: number;
  /**
   * Timeout for non-GET requests, when the caller passes no explicit per-request timeout.
   * Writes get a longer leash than reads on purpose (ATL-776): aborting a write client-side
   * does not stop the server from finishing it — it only guarantees the client never learns
   * the outcome, turning a slow success into a lost dual-write. Reads keep `timeout`.
   */
  writeTimeout?: number;
  /** Called when response validation fails */
  onResponseValidationError?: (error: z.ZodError, path: string) => void;
  /** Called when request validation fails (pre-flight) */
  onRequestValidationError?: (error: z.ZodError, path: string) => void;
  /** If true, throws on validation errors in production too (default: only in dev) */
  strictValidation?: boolean;
  /**
   * The axios transport. Omitted = axios' default (the network). `captureWriteRequest` passes one
   * that never sends, to obtain a request exactly as `request()` would issue it (ATL-1503).
   */
  adapter?: AxiosRequestConfig['adapter'];
}

/**
 * Convert Date objects to ISO strings for API serialization, and defensively strip live
 * Firestore refs / cyclical structures so the request body can't stack-overflow this recursion
 * ("RangeError: Maximum call stack size exceeded" — Sentry QARASAY-KASSA-26F/26G) before axios
 * runs. Delegates to the shared cycle-safe sanitizer; a normal DTO round-trips unchanged.
 */
function serializeDates<T>(data: T): T {
  return sanitizeRequestBody(data);
}

/**
 * Known date field names that should be converted to TimestampLike objects.
 * Matches TIMESTAMP_FIELDS from Firebase/types.ts
 */
const DATE_FIELDS = new Set([
  'date',
  'saleDate',
  'supplyDate',
  'createdDate',
  'createdAt',
  'updatedAt',
  'lastChangeAt',
  'registrationDate',
  'lastPurchaseDate',
  'paymentDate',
  'startDate',
  'endDate',
  'linkedDocDate',
  'clientSaveDate',
]);

import { postgresTimestampToIso } from './postgres-timestamp';

/**
 * TimestampLike object that mimics Firestore Timestamp behavior.
 */
interface TimestampLike {
  toDate: () => Date;
  seconds: number;
  nanoseconds: number;
}

/**
 * Convert ISO date string to TimestampLike object for Firestore compatibility.
 *
 * ATL-1313 — a string this cannot parse is returned UNCHANGED rather than wrapped.
 *
 * `isIsoDateString` below is deliberately loose: its `T…` group is optional, so any value starting
 * `YYYY-MM-DD` matches — including Postgres's own `timestamptz` text (`2026-09-09 23:29:55.534+00`),
 * which `transactions.payment_range` really holds. Wrapping such a value used to produce a
 * `TimestampLike` closing over an `Invalid Date`, with `seconds`/`nanoseconds` both `NaN`, and the
 * original string was then unrecoverable. That is worse than not converting, in two different ways:
 *
 *   - a consumer calling `.toDate()` gets a TRUTHY Invalid Date, which `date-fns` `format()` answers
 *     by throwing `RangeError: Invalid time value` — fatal inside JSX, which is the ATL-1313 crash;
 *   - a consumer that guards (`toNestedDate`) can only answer "unknown", so a date that was fully
 *     recoverable from the string renders as a dash.
 *
 * Whether `new Date` parses it is engine-dependent — V8 accepts the Postgres form, Hermes does not —
 * so the same payload converted cleanly on web and became an Invalid Date on a phone. Handing the
 * string on lets the consumer decode it (`apps/atlas/utils/nestedDateValue.ts`) instead of guessing
 * from a `NaN`.
 */
function toTimestampLike(isoString: string): TimestampLike | string {
  // ATL-1313: normalise BEFORE `new Date`, so a Postgres-rendered `timestamptz` converts on every
  // engine instead of only on V8 — and so the string branch below is reached ONLY by a value no
  // decoder anywhere could read. That distinction is load-bearing: two consumers
  // (`ClientsScreen.dateText`, `SupplyOrderDetailScreen`'s `?.toDate?.() || new Date()`) handle a
  // raw string WORSE than they handled the old Invalid-Date wrapper — one throws inside JSX, the
  // other substitutes today's date and saves it. Narrowing the escape to the genuinely
  // undecodable is what keeps this fix from trading one crash for another.
  const date = new Date(postgresTimestampToIso(isoString) ?? isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return {
    toDate: () => date,
    seconds: Math.floor(date.getTime() / 1000),
    nanoseconds: (date.getTime() % 1000) * 1000000,
  };
}

/**
 * Check if a string looks like an ISO date.
 */
function isIsoDateString(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  // Match ISO 8601 date format
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/.test(value);
}

/**
 * Recursively convert known date fields from ISO strings to TimestampLike objects.
 * This ensures frontend receives data compatible with Firestore Timestamp API.
 * Exported for testing. Cycle-safe (defensive — see deserializeDatesInner).
 */
export function deserializeDates<T>(data: T): T {
  return deserializeDatesInner(data, new WeakSet()) as T;
}

function deserializeDatesInner(data: unknown, seen: WeakSet<object>): unknown {
  if (data === null || typeof data !== 'object') return data;
  // Cycle guard: responses from a trusted backend shouldn't cycle, but never stack-overflow
  // this recursion (RangeError) the way the request path did (Sentry 26F/26G).
  if (seen.has(data)) return undefined;
  seen.add(data);

  let result: unknown;
  if (Array.isArray(data)) {
    result = data.map((value) => deserializeDatesInner(value, seen));
  } else {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (DATE_FIELDS.has(key) && isIsoDateString(value)) {
        out[key] = toTimestampLike(value);
      } else {
        out[key] = deserializeDatesInner(value, seen);
      }
    }
    result = out;
  }

  seen.delete(data);
  return result;
}

/** axios request config tagged so the 401 retry interceptor only replays once. */
interface RetryableRequestConfig extends AxiosRequestConfig {
  _retriedAfter401?: boolean;
}

export class TypedApiClient {
  private axios: AxiosInstance;
  private getAuthToken: (forceRefresh?: boolean) => Promise<string | null>;
  private onResponseValidationError?: (error: z.ZodError, path: string) => void;
  private onRequestValidationError?: (error: z.ZodError, path: string) => void;
  private strictValidation: boolean;
  private writeTimeout?: number;

  constructor(config: TypedApiClientConfig) {
    this.getAuthToken = config.getAuthToken;
    this.onResponseValidationError = config.onResponseValidationError;
    this.onRequestValidationError = config.onRequestValidationError;
    this.strictValidation = config.strictValidation ?? false;
    this.writeTimeout = config.writeTimeout;

    this.axios = axios.create({
      baseURL: config.baseURL,
      ...(config.adapter ? { adapter: config.adapter } : {}),
      timeout: config.timeout ?? 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Auth interceptor — the single source of the bearer header. On a 401 replay
    // (flagged by the response interceptor below) it forces a token refresh so the
    // replay never re-attaches the stale token (ATL-526).
    this.axios.interceptors.request.use(async (reqConfig) => {
      try {
        const forceRefresh = (reqConfig as RetryableRequestConfig)._retriedAfter401 === true;
        const token = await this.getAuthToken(forceRefresh);
        if (token) {
          reqConfig.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error);
      }
      return reqConfig;
    });

    // 401 retry interceptor (ATL-526): a cached Firebase ID token can be stale by the
    // time the request reaches the backend (1h lifetime; the SDK only auto-refreshes
    // within 5 min of expiry, so clock skew / borderline expiry slip through). On a
    // single 401, flag the request and replay it exactly once — the request
    // interceptor then forces a fresh token for the replay.
    this.axios.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        if (!axios.isAxiosError(error) || error.response?.status !== 401) {
          return Promise.reject(error);
        }
        const originalRequest = error.config as RetryableRequestConfig | undefined;
        if (!originalRequest || originalRequest._retriedAfter401) {
          return Promise.reject(error);
        }
        originalRequest._retriedAfter401 = true;
        return this.axios.request(originalRequest);
      },
    );
  }

  /**
   * Type-safe request with optional request body validation
   */
  async request<TRequest, TResponse>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    options: {
      data?: TRequest;
      params?: Record<string, unknown>;
      requestSchema?: ZodSchema<TRequest>;
      responseSchema: ZodSchema<TResponse>;
      config?: AxiosRequestConfig;
    },
  ): Promise<TResponse> {
    // Serialize Date objects to ISO strings before validation
    const serializedData = options.data !== undefined ? serializeDates(options.data) : undefined;

    // Pre-flight request validation
    if (options.requestSchema && serializedData !== undefined) {
      const requestParsed = options.requestSchema.safeParse(serializedData);

      if (!requestParsed.success) {
        console.error(
          `API request validation failed for ${method} ${path}:`,
          requestParsed.error.errors,
        );

        if (this.onRequestValidationError) {
          this.onRequestValidationError(requestParsed.error, path);
        }

        // Always throw on request validation errors - don't send invalid data
        throw new ApiValidationError(
          `Request validation failed: ${requestParsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
          requestParsed.error.errors,
          'request',
          path,
        );
      }
    }

    try {
      const response = await this.axios.request({
        method,
        url: path,
        data: serializedData,
        params: options.params,
        // Writes get the longer write timeout (ATL-776); an explicit per-request
        // config.timeout (spread below) still wins over both defaults.
        ...(method !== 'GET' && this.writeTimeout !== undefined
          ? { timeout: this.writeTimeout }
          : {}),
        ...options.config,
      });

      // Runtime response validation with Zod
      const parsed = options.responseSchema.safeParse(response.data);

      if (!parsed.success) {
        console.error(
          `API response validation failed for ${method} ${path}:`,
          parsed.error.errors,
        );

        if (this.onResponseValidationError) {
          this.onResponseValidationError(parsed.error, path);
        }

        // In strict mode, throw on validation errors (use strictValidation: __DEV__ in RN)
        if (this.strictValidation) {
          throw new ApiValidationError(
            `Response validation failed: ${parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
            parsed.error.errors,
            'response',
            path,
          );
        }
      }

      // Convert date strings to TimestampLike objects for Firestore compatibility
      return deserializeDates(response.data) as TResponse;
    } catch (error) {
      // The request as sent, attached to whatever we throw so the dual-write outbox
      // (ATL-782) can persist and later re-issue the identical write. Built from the
      // caller-level values (path + pre-transform body), not axios' config, so the
      // replay does not depend on axios internals.
      const requestConfig: ReplayableRequestConfig = {
        method,
        url: path,
        data: serializedData,
        params: options.params,
      };
      // Handle Axios errors and wrap them in typed errors
      // Use axios.isAxiosError() instead of instanceof to avoid bundler issues in React Native
      if (axios.isAxiosError(error) && error.response) {
        // Unwraps the backend's AllExceptionsFilter envelope (nested ValidationPipe
        // string[] message) so the real constraint text survives; falls back to the
        // generic axios message only for truly non-standard bodies.
        const apiError = normalizeApiErrorBody(
          error.response.data,
          error.response.status,
          error.response.statusText,
          error.message,
        );
        const wrapped = new ApiRequestError(apiError.message, apiError.statusCode, apiError);
        wrapped.requestConfig = requestConfig;
        throw wrapped;
      }
      if (axios.isAxiosError(error)) {
        // Network / timeout failure — never reached the backend (or the response was lost).
        // Patch the replayable request onto the raw error for the outbox.
        (error as Error & { requestConfig?: ReplayableRequestConfig }).requestConfig =
          requestConfig;
      }
      throw error;
    }
  }

  /**
   * Re-issue a previously captured request verbatim (dual-write outbox replay, ATL-782).
   *
   * Deliberately schema-free: the payload already passed request validation on the original
   * attempt, the backend re-validates regardless, and the caller does not consume the body.
   * Runs through the same axios instance, so the auth interceptor attaches a FRESH bearer
   * token and the 401-retry interceptor applies — a replay hours after enqueue must not
   * depend on the original (long-expired) token. Errors propagate as raw axios errors /
   * `ApiRequestError` is NOT applied here; the outbox classifies transport errors itself.
   */
  async replayRequest<TResponse = unknown>(
    config: ReplayableRequestConfig & { timeout?: number },
    options: ReplayRequestOptions = {},
  ): Promise<TResponse> {
    const response = await this.axios.request({
      method: config.method,
      url: config.url,
      data: config.data,
      params: config.params,
      timeout:
        config.timeout ??
        (config.method !== 'GET' ? this.writeTimeout : undefined),
      // Set per SEND, never part of the persisted config (which carries no headers at all).
      ...(options.writeMode !== undefined
        ? { headers: { [WRITE_MODE_HEADER]: writeModeHeaderValue(options.writeMode) } }
        : {}),
    });
    return response.data as TResponse;
  }

  // Convenience methods with typed request bodies
  async get<TResponse>(
    path: string,
    options: {
      params?: Record<string, unknown>;
      responseSchema: ZodSchema<TResponse>;
    },
  ): Promise<TResponse> {
    return this.request<never, TResponse>('GET', path, options);
  }

  // `params` is accepted on every verb, not just GET (ATL-843). `request()` has always
  // forwarded it to axios — which encodes query values correctly — but the write verbs did
  // not expose it, so a write needing a query parameter had to hand-roll the string onto
  // the path. That is how `/products/{id}/stock?userId=…` came to be built by concatenation,
  // and hand-rolled URL assembly is the defect class this file's `apiPath` exists to end.
  async post<TRequest, TResponse>(
    path: string,
    options: {
      data: TRequest;
      params?: Record<string, unknown>;
      requestSchema?: ZodSchema<TRequest>;
      responseSchema: ZodSchema<TResponse>;
    },
  ): Promise<TResponse> {
    return this.request<TRequest, TResponse>('POST', path, options);
  }

  async put<TRequest, TResponse>(
    path: string,
    options: {
      data: TRequest;
      params?: Record<string, unknown>;
      requestSchema?: ZodSchema<TRequest>;
      responseSchema: ZodSchema<TResponse>;
    },
  ): Promise<TResponse> {
    return this.request<TRequest, TResponse>('PUT', path, options);
  }

  async patch<TRequest, TResponse>(
    path: string,
    options: {
      data: TRequest;
      params?: Record<string, unknown>;
      requestSchema?: ZodSchema<TRequest>;
      responseSchema: ZodSchema<TResponse>;
    },
  ): Promise<TResponse> {
    return this.request<TRequest, TResponse>('PATCH', path, options);
  }

  async delete<TResponse>(
    path: string,
    options: {
      params?: Record<string, unknown>;
      responseSchema: ZodSchema<TResponse>;
    },
  ): Promise<TResponse> {
    return this.request<never, TResponse>('DELETE', path, options);
  }
}

// URL path-segment encoding (ATL-843) — every endpoint below builds its paths with `apiPath`.
export { postgresTimestampToIso } from './postgres-timestamp';
export { captureWriteRequest, RequestNotCapturedError } from './capture';
// ATL-1445 stage 3 (P8): the write-master refusal contract backend PR B1 implements.
export {
  parseWriteMasterSupabaseRefusal,
  WRITE_MASTER_FIRESTORE_CODE,
  WRITE_MASTER_SUPABASE_CODE,
  type WriteMasterSupabaseRefusal,
  type WriteMasterSupabaseRefusalBody,
} from './write-master-refusal';
// ATL-1477 snapshot windows — read a whole list and prove it (see ./id-window.ts).
export { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from './id-window';
export { apiPath, encodePathSegment, InvalidPathSegmentError } from './path';

// Re-export endpoint creators and types
// Sales
export {
  createSalesApi,
  SaleResponseSchema,
  CreateSaleSchema,
  UpdateSaleSchema,
  CartItemSchema,
  VariantSchema,
  FiscalInfoSchema,
  ReturnInfoSchema,
  ModifierSchema,
  DeleteResponseSchema,
} from './endpoints/sales';
export type {
  SaleResponse,
  CreateSale,
  UpdateSale,
  DeleteResponse,
  CartItem,
  Variant,
  FiscalInfo,
  ReturnInfo,
  Modifier,
} from './endpoints/sales';

// Products
export { createProductsApi } from './endpoints/products';
export type {
  ProductResponse,
  ProductWithStockResponse,
  CreateProduct,
} from './endpoints/products';

// Clients
export { createClientsApi } from './endpoints/clients';
export type {
  ClientResponse,
  ClientPunchCardResponse,
  ClientStatsResponse,
  CreateClient,
} from './endpoints/clients';

// Stores
export {
  createStoresApi,
  StoreResponseSchema,
  CreateStoreSchema,
  UpdateStoreSchema,
  TimeSchema,
  TimezoneSchema,
  FiscalOperatorSchema,
} from './endpoints/stores';
export type {
  StoreResponse,
  CreateStore,
  UpdateStore,
  Time,
} from './endpoints/stores';

// Organizations
export {
  createOrganizationsApi,
  OrganizationResponseSchema,
  UpdateOrganizationSchema,
  OrganizationComparisonResultSchema,
  SubscriptionSyncResultSchema,
  OverrideSubscriptionSchema,
  CurrencySchema,
  BusinessTypeSchema,
} from './endpoints/organizations';
export type {
  OrganizationResponse,
  UpdateOrganization,
  OrganizationComparisonResult,
  SubscriptionSyncResult,
  OverrideSubscription,
} from './endpoints/organizations';

// Transactions
export {
  createTransactionsApi,
  TransactionResponseSchema,
  CreateTransactionSchema,
} from './endpoints/transactions';
export type {
  TransactionResponse,
  CreateTransaction,
} from './endpoints/transactions';

// Supplies
export {
  createSuppliesApi,
  SupplyResponseSchema,
  CreateSupplySchema,
  UpdateSupplySchema,
  SupplyItemSchema,
} from './endpoints/supplies';
export type {
  SupplyResponse,
  CreateSupply,
  UpdateSupply,
  SupplyItem,
} from './endpoints/supplies';

// Transfers
export {
  createTransfersApi,
  TransferResponseSchema,
  CreateTransferSchema,
  TransferItemSchema,
} from './endpoints/transfers';
export type {
  TransferResponse,
  CreateTransfer,
  TransferItem,
} from './endpoints/transfers';

// Writeoffs
export {
  createWriteoffsApi,
  WriteoffResponseSchema,
  CreateWriteoffSchema,
  WriteoffItemSchema,
} from './endpoints/writeoffs';
export type {
  WriteoffResponse,
  CreateWriteoff,
  WriteoffItem,
} from './endpoints/writeoffs';

// Revisions
export {
  createRevisionsApi,
  RevisionResponseSchema,
  CreateRevisionSchema,
  UpdateRevisionSchema,
  RevisionItemSchema,
} from './endpoints/revisions';
export type {
  RevisionResponse,
  CreateRevision,
  UpdateRevision,
  RevisionItem,
} from './endpoints/revisions';

// Spendings
export {
  createSpendingsApi,
  SpendingResponseSchema,
  CreateSpendingSchema,
} from './endpoints/spendings';
export type {
  SpendingResponse,
  CreateSpending,
} from './endpoints/spendings';

// Working Hours
export {
  createWorkingHoursApi,
  WorkingHoursResponseSchema,
  CreateWorkingHoursSchema,
  UpdateWorkingHoursSchema,
  BulkWorkingHoursResponseSchema,
  RoleDetailsSchema,
} from './endpoints/working-hours';
export type {
  WorkingHoursResponse,
  CreateWorkingHours,
  UpdateWorkingHours,
  BulkWorkingHoursResponse,
} from './endpoints/working-hours';

// Auth
export {
  createAuthApi,
  AuthUserSchema,
  SessionSchema,
  SignupResponseSchema,
  LoginResponseSchema,
  MessageResponseSchema,
  SignupSchema,
  LoginSchema,
} from './endpoints/auth';
export type {
  AuthUser,
  Session,
  SignupResponse,
  LoginResponse,
  MessageResponse,
  Signup,
  Login,
} from './endpoints/auth';

// Product Categories
export {
  createProductCategoriesApi,
  ProductCategoryResponseSchema,
  CreateProductCategorySchema,
  UpdateProductCategorySchema,
} from './endpoints/product-categories';
export type {
  ProductCategoryResponse,
  CreateProductCategory,
  UpdateProductCategory,
} from './endpoints/product-categories';

// Suppliers
export {
  createSuppliersApi,
  SupplierResponseSchema,
  CreateSupplierSchema,
  UpdateSupplierSchema,
} from './endpoints/suppliers';
export type {
  SupplierResponse,
  CreateSupplier,
  UpdateSupplier,
} from './endpoints/suppliers';

// Wallets
export {
  createWalletsApi,
  WalletResponseSchema,
  CreateWalletSchema,
  UpdateWalletSchema,
} from './endpoints/wallets';
export type {
  WalletResponse,
  CreateWallet,
  UpdateWallet,
} from './endpoints/wallets';

// Payment Methods
export {
  createPaymentMethodsApi,
  PaymentMethodResponseSchema,
  CreatePaymentMethodSchema,
  UpdatePaymentMethodSchema,
} from './endpoints/payment-methods';
export type {
  PaymentMethodResponse,
  CreatePaymentMethod,
  UpdatePaymentMethod,
} from './endpoints/payment-methods';

// Roles
export {
  createRolesApi,
  RoleResponseSchema,
  CreateRoleSchema,
  UpdateRoleSchema,
} from './endpoints/roles';
export type {
  RoleResponse,
  CreateRole,
  UpdateRole,
} from './endpoints/roles';

// Product Units
export {
  createProductUnitsApi,
  ProductUnitResponseSchema,
  CreateProductUnitSchema,
  UpdateProductUnitSchema,
} from './endpoints/product-units';
export type {
  ProductUnitResponse,
  CreateProductUnit,
  UpdateProductUnit,
} from './endpoints/product-units';

// Cashbox Transactions
export {
  createCashboxTransactionsApi,
  CashboxTransactionResponseSchema,
  CreateCashboxTransactionSchema,
  UpdateCashboxTransactionSchema,
} from './endpoints/cashbox-transactions';
export type {
  CashboxTransactionResponse,
  CreateCashboxTransaction,
  UpdateCashboxTransaction,
} from './endpoints/cashbox-transactions';

// Price Types
export {
  createPriceTypesApi,
  PriceTypeResponseSchema,
  CreatePriceTypeSchema,
  UpdatePriceTypeSchema,
} from './endpoints/price-types';
export type {
  PriceTypeResponse,
  CreatePriceType,
  UpdatePriceType,
} from './endpoints/price-types';

// Users
export {
  createUsersApi,
  UserResponseSchema,
  UpdateUserSchema,
} from './endpoints/users';
export type {
  UserResponse,
  UpdateUser,
} from './endpoints/users';

// Audit Log
export {
  createAuditLogApi,
  AuditLogResponseSchema,
  CreateAuditLogSchema,
} from './endpoints/audit-log';
export type {
  AuditLogResponse,
  CreateAuditLog,
} from './endpoints/audit-log';

// Saved Carts
export {
  createSavedCartsApi,
  SavedCartResponseSchema,
  CreateSavedCartSchema,
  UpdateSavedCartSchema,
} from './endpoints/saved-carts';
export type {
  SavedCartResponse,
  CreateSavedCart,
  UpdateSavedCart,
} from './endpoints/saved-carts';

// Checklists
export {
  createChecklistsApi,
  ChecklistResponseSchema,
  CreateChecklistSchema,
  UpdateChecklistSchema,
} from './endpoints/checklists';
export type {
  ChecklistResponse,
  CreateChecklist,
  UpdateChecklist,
} from './endpoints/checklists';

// Complex Sales
export {
  createComplexSalesApi,
  ComplexSaleResponseSchema,
  CreateComplexSaleSchema,
  UpdateComplexSaleSchema,
} from './endpoints/complex-sales';
export type {
  ComplexSaleResponse,
  CreateComplexSale,
  UpdateComplexSale,
} from './endpoints/complex-sales';

// Complex Sale Templates
export {
  createComplexSaleTemplatesApi,
  ComplexSaleTemplateResponseSchema,
  CreateComplexSaleTemplateSchema,
  UpdateComplexSaleTemplateSchema,
} from './endpoints/complex-sale-templates';
export type {
  ComplexSaleTemplateResponse,
  CreateComplexSaleTemplate,
  UpdateComplexSaleTemplate,
} from './endpoints/complex-sale-templates';

// Supply Orders
export {
  createSupplyOrdersApi,
  SupplyOrderResponseSchema,
  CreateSupplyOrderSchema,
  UpdateSupplyOrderSchema,
  SupplyOrderItemSchema,
} from './endpoints/supply-orders';
export type {
  SupplyOrderResponse,
  CreateSupplyOrder,
  UpdateSupplyOrder,
  SupplyOrderItem,
} from './endpoints/supply-orders';

// Manufactures
export {
  createManufacturesApi,
  ManufactureResponseSchema,
  CreateManufactureSchema,
  ManufactureIngredientSchema,
} from './endpoints/manufactures';
export type {
  ManufactureResponse,
  CreateManufacture,
  ManufactureIngredient,
} from './endpoints/manufactures';

// Print Templates
export {
  createPrintTemplatesApi,
  PrintTemplateResponseSchema,
  CreatePrintTemplateSchema,
  UpdatePrintTemplateSchema,
} from './endpoints/print-templates';
export type {
  PrintTemplateResponse,
  CreatePrintTemplate,
  UpdatePrintTemplate,
} from './endpoints/print-templates';

// Product Tags
export {
  createProductTagsApi,
  ProductTagResponseSchema,
  CreateProductTagSchema,
  UpdateProductTagSchema,
} from './endpoints/product-tags';
export type {
  ProductTagResponse,
  CreateProductTag,
  UpdateProductTag,
} from './endpoints/product-tags';

// Client Operations
export {
  createClientOperationsApi,
  ClientOperationResponseSchema,
  CreateClientOperationSchema,
} from './endpoints/client-operations';
export type {
  ClientOperationResponse,
  CreateClientOperation,
} from './endpoints/client-operations';

// Settings
export {
  createSettingsApi,
  LoyaltySettingsResponseSchema,
  MarketplaceSettingsResponseSchema,
  UpdateLoyaltySettingsSchema,
  UpdateMarketplaceSettingsSchema,
} from './endpoints/settings';
export type {
  LoyaltySettingsResponse,
  MarketplaceSettingsResponse,
  UpdateLoyaltySettings,
  UpdateMarketplaceSettings,
} from './endpoints/settings';

// Sale Settings
export {
  createSaleSettingsApi,
  SaleSettingsResponseSchema,
  CreateSaleSettingsSchema,
  UpdateSaleSettingsSchema,
} from './endpoints/sale-settings';
export type {
  SaleSettingsResponse,
  CreateSaleSettings,
  UpdateSaleSettings,
} from './endpoints/sale-settings';

// Loyalty Settings (dedicated CRUD module at /loyalty-settings)
export { createLoyaltySettingsApi } from './endpoints/loyalty-settings';
export {
  LoyaltySettingsResponseSchema as LoyaltySettingsCrudResponseSchema,
  CreateLoyaltySettingsSchema,
} from './endpoints/loyalty-settings';
export type {
  LoyaltySettingsResponse as LoyaltySettingsCrudResponse,
  CreateLoyaltySettings,
} from './endpoints/loyalty-settings';

// Orders
export {
  createOrdersApi,
  OrderResponseSchema,
  UpdateOrderSchema,
} from './endpoints/orders';
export type {
  OrderResponse,
  UpdateOrder,
} from './endpoints/orders';

// Product History
export {
  createProductHistoryApi,
  ProductHistoryResponseSchema,
  CreateProductHistorySchema,
} from './endpoints/product-history';
export type {
  ProductHistoryResponse,
  CreateProductHistory,
} from './endpoints/product-history';

// Spending Categories
export {
  createSpendingCategoriesApi,
  SpendingCategoryResponseSchema,
  CreateSpendingCategorySchema,
  UpdateSpendingCategorySchema,
} from './endpoints/spending-categories';
export type {
  SpendingCategoryResponse,
  CreateSpendingCategory,
  UpdateSpendingCategory,
} from './endpoints/spending-categories';

// Discount Options
export {
  createDiscountOptionsApi,
  DiscountOptionResponseSchema,
  CreateDiscountOptionSchema,
  UpdateDiscountOptionSchema,
} from './endpoints/discount-options';
export type {
  DiscountOptionResponse,
  CreateDiscountOption,
  UpdateDiscountOption,
} from './endpoints/discount-options';

// Write-Off Reasons
export {
  createWriteOffReasonsApi,
  WriteOffReasonResponseSchema,
  CreateWriteOffReasonSchema,
  UpdateWriteOffReasonSchema,
} from './endpoints/write-off-reasons';
export type {
  WriteOffReasonResponse,
  CreateWriteOffReason,
  UpdateWriteOffReason,
} from './endpoints/write-off-reasons';

// Return Reasons
export {
  createReturnReasonsApi,
  ReturnReasonResponseSchema,
  CreateReturnReasonSchema,
  UpdateReturnReasonSchema,
} from './endpoints/return-reasons';
export type {
  ReturnReasonResponse,
  CreateReturnReason,
  UpdateReturnReason,
} from './endpoints/return-reasons';

// Purchase Methods
export {
  createPurchaseMethodsApi,
  PurchaseMethodResponseSchema,
  CreatePurchaseMethodSchema,
  UpdatePurchaseMethodSchema,
} from './endpoints/purchase-methods';
export type {
  PurchaseMethodResponse,
  CreatePurchaseMethod,
  UpdatePurchaseMethod,
} from './endpoints/purchase-methods';

// Product Modifiers
export {
  createProductModifiersApi,
  ProductModifierResponseSchema,
  CreateProductModifierSchema,
  UpdateProductModifierSchema,
} from './endpoints/product-modifiers';
export type {
  ProductModifierResponse,
  CreateProductModifier,
  UpdateProductModifier,
} from './endpoints/product-modifiers';

// Recurring Spendings
export {
  createRecurringSpendingsApi,
  RecurringSpendingResponseSchema,
  CreateRecurringSpendingSchema,
  UpdateRecurringSpendingSchema,
} from './endpoints/recurring-spendings';
export type {
  RecurringSpendingResponse,
  CreateRecurringSpending,
  UpdateRecurringSpending,
} from './endpoints/recurring-spendings';

// Product Movements
export {
  createProductMovementsApi,
  ProductMovementResponseSchema,
} from './endpoints/product-movements';
export type {
  ProductMovementResponse,
} from './endpoints/product-movements';

// Reports (daily stats, product stats)
export {
  createReportsApi,
  DailyStatsResponseSchema,
  ProductStatsResponseSchema,
} from './endpoints/reports';
export type {
  DailyStatsResponse,
  ProductStatsResponse,
} from './endpoints/reports';

// Updates (operation changelog)
export {
  createUpdatesApi,
  UpdateResponseSchema,
  CreateUpdateSchema,
} from './endpoints/updates';
export type {
  UpdateResponse,
  CreateUpdate,
} from './endpoints/updates';

// Marketplace (public storefront reads, no auth)
export {
  createMarketplaceApi,
  MarketplaceOrganizationSchema,
  MarketplaceProductSchema,
  MarketplaceCategorySchema,
  MarketplaceUnitSchema,
  MarketplacePriceTypeSchema,
} from './endpoints/marketplace';
export type {
  MarketplaceOrganization,
  MarketplaceProduct,
  MarketplaceCategory,
  MarketplaceUnit,
  MarketplacePriceType,
} from './endpoints/marketplace';
